/**
 * Derive display-friendly strings for the order ETA:
 *  - `formattedEta`: localized HH:MM time-of-day (or the raw ISO if parsing fails).
 *  - `timeRemaining`: countdown like "12 min" / "1h 5m" / localized "any moment now".
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface EstimatedTimeDisplay {
  formattedEta: string | null
  timeRemaining: string | null
}

export function useEstimatedTimeDisplay(
  estimatedTime: string | null | undefined
): EstimatedTimeDisplay {
  const { t } = useTranslation()

  const formattedEta = useMemo(() => {
    if (!estimatedTime) return null
    try {
      const date = new Date(estimatedTime)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch {
      return estimatedTime
    }
  }, [estimatedTime])

  const timeRemaining = useMemo(() => {
    if (!estimatedTime) return null
    try {
      const eta = new Date(estimatedTime)
      const now = new Date()
      const diffMs = eta.getTime() - now.getTime()
      if (diffMs <= 0) return t('common:anyMomentNow')
      const diffMins = Math.ceil(diffMs / 60000)
      if (diffMins < 60) return `${diffMins} min`
      const hours = Math.floor(diffMins / 60)
      const mins = diffMins % 60
      return `${hours}h ${mins}m`
    } catch {
      return null
    }
  }, [estimatedTime, t])

  return { formattedEta, timeRemaining }
}
